import type { TrackingConsent } from '@datadog/browser-rum';
import { datadogRum } from '@datadog/browser-rum';
import { getUserConsentPreferences } from '@cbhq/ucpm-web';

export type RumViewName =
  | 'team'
  | 'team_member'
  | 'team_pending_invite'
  | 'team_invite'
  | 'activity'
  | 'security'
  | 'security_approval_rules'
  | 'approvals';

// Cached so rumSetUser can reuse it without reading back the untyped getUser().
let cachedDeviceId = '';

// RUM and its _dd_s cookie run only after the user accepts the Performance
// category in the cookie banner (PRIVREQ-5962).
export function toRumTrackingConsent(preferences: Record<string, boolean>): TrackingConsent {
  return preferences.performance === true ? 'granted' : 'not-granted';
}

export function getRumTrackingConsent(): TrackingConsent {
  try {
    return toRumTrackingConsent(getUserConsentPreferences());
  } catch {
    return 'not-granted';
  }
}

export function rumSetTrackingConsent(consent: TrackingConsent) {
  datadogRum.setTrackingConsent(consent);
}

export function rumStartView(name: RumViewName) {
  datadogRum.startView({ name });
}

// Mirror north-star business outcomes onto the RUM timeline so they correlate
// with views, errors, and replay. Only pass non-PII enums/booleans as context.
export function rumAddAction(name: string, context?: Record<string, unknown>) {
  datadogRum.addAction(name, context);
}

export function rumSetDevice(deviceId: string) {
  cachedDeviceId = deviceId;

  // Set the user context to the device ID to allow for anonymous sessions
  datadogRum.setUser({
    id: deviceId,
    identity_device_id: deviceId,
    anonymous: true,
  });

  datadogRum.setGlobalContext({
    identity_device_id: deviceId,
  });
}

export function rumSetUser(userId: string) {
  // The user session is now associated with a user ID and device ID.
  // This will not split the session.
  datadogRum.setUser({
    id: userId,
    identity_device_id: cachedDeviceId,
    anonymous: false,
  });
}
